import { motion } from "framer-motion";
import { RotateCcw, Clock, MessageCircle, Flame } from "lucide-react";
import { MicroDemo } from "../landing/MicroDemo";

const features = [
  {
    icon: RotateCcw,
    title: "One-tap reset",
    description: "Surface what you were doing. No digging required.",
    accent: "#0CBCBF"
  },
  {
    icon: Clock,
    title: "Let unfinished things return",
    description: "In progress tasks are tracked with gentle reminder indicators.",
    accent: "#0CBCBF"
  },
  {
    icon: MessageCircle,
    title: "Kind prompts to re-engage",
    description: "No shame spirals. Just gentle nudges forward.",
    accent: "#F58227"
  },
  {
    icon: Flame,
    title: "Encourage focus time",
    description: "Each task feeds the flame—a micro-win that reminds you you're moving forward.",
    accent: "#F58227"
  }
];

export const HowItWorksSection = () => {
  return (
    <section 
      id="how-it-works"
      data-testid="how-it-works-section"
      className="py-14 md:py-20 px-6 md:px-12 bg-gradient-to-b from-[#FDFBF7] to-white"
    >
      <div className="max-w-5xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-12 text-center"
        >
          <p className="text-sm font-medium uppercase tracking-widest text-[#0CBCBF] mb-4">
            What's inside
          </p>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-[#27272A] mb-4">
            A companion, not a manager
          </h2>
          <p className="text-[#52525B] max-w-lg mx-auto">
            Tools that meet you where you are — without optimization pressure.
          </p>
        </motion.div>

        {/* Features grid with MicroDemo */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {/* Micro Demo - takes place of one card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="md:col-span-1 lg:row-span-2"
          >
            <MicroDemo />
          </motion.div>

          {/* Feature cards - with depth */}
          {features.map((feature, index) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: (index + 1) * 0.1 }}
              data-testid={`feature-card-${index}`}
              className="bg-white p-6 rounded-2xl group hover:-translate-y-1 transition-all relative overflow-hidden"
              style={{
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04), 0 4px 12px rgba(0, 0, 0, 0.06), 0 12px 32px rgba(0, 0, 0, 0.05)'
              }}
            >
              {/* Card surface depth */}
              <div 
                className="absolute inset-0 pointer-events-none rounded-2xl"
                style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.8) 0%, transparent 40%)' }}
              />
              <div 
                className="absolute inset-0 pointer-events-none rounded-2xl"
                style={{ background: 'linear-gradient(0deg, rgba(0,0,0,0.02) 0%, transparent 30%)' }}
              />
              
              <div 
                className="w-10 h-10 rounded-xl flex items-center justify-center mb-4 relative z-10"
                style={{ backgroundColor: `${feature.accent}15` }}
              >
                <feature.icon size={20} style={{ color: feature.accent }} />
              </div>
              <h3 className="text-lg font-semibold text-[#27272A] mb-2 relative z-10">
                {feature.title}
              </h3>
              <p className="text-sm text-[#52525B] leading-relaxed relative z-10">
                {feature.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
